import { Helmet } from 'react-helmet-async';

export default function PressPage() {
  const pressReleases = [
    {
      date: 'January 15, 2026',
      title: 'BESTIX Launches Enhanced Buyer Guarantee Program',
      excerpt: 'New protection features ensure 100% authentic tickets and on-time delivery for all purchases.'
    },
    {
      date: 'December 10, 2025',
      title: 'BESTIX Partners with Major League Baseball for Official Resale',
      excerpt: 'Exclusive partnership brings verified MLB tickets to millions of fans.'
    },
    {
      date: 'November 5, 2025',
      title: 'BESTIX Expands to European Markets',
      excerpt: 'UK, France, and Germany launch marks major international expansion.'
    },
    {
      date: 'October 1, 2025',
      title: 'BESTIX Raises $50M Series B to Accelerate Growth',
      excerpt: 'Funding led by top-tier investors will fuel product development and market expansion.'
    }
  ];

  const stats = [
    { value: '$500M+', label: 'Transaction Volume' },
    { value: '10M+', label: 'Tickets Sold' },
    { value: '50K+', label: 'Events Listed' },
    { value: '99.9%', label: 'Customer Satisfaction' }
  ];

  const mediaAssets = [
    { name: 'Logo Pack', format: 'ZIP', size: '2.4 MB' },
    { name: 'Brand Guidelines', format: 'PDF', size: '8.1 MB' },
    { name: 'Executive Photos', format: 'ZIP', size: '15.2 MB' },
    { name: 'Product Screenshots', format: 'ZIP', size: '12.8 MB' }
  ];

  return (
    <>
      <Helmet><title>Press | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Press Room</h1>
            <p className="mt-4 text-xl text-gray-300">News, resources, and media inquiries</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          {/* Stats */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <h2 className="text-xl font-semibold text-navy text-center mb-8">BESTIX by the Numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gold mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Press Releases */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-semibold text-navy mb-6">Press Releases</h2>
              <div className="space-y-4">
                {pressReleases.map((release, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer">
                    <div className="text-sm text-gray-400 mb-2">{release.date}</div>
                    <h3 className="font-semibold text-navy mb-2">{release.title}</h3>
                    <p className="text-sm text-gray-600">{release.excerpt}</p>
                    <button className="text-sm text-gold font-medium mt-3 hover:underline">
                      Read More →
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Media Contact */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-semibold text-navy mb-4">Media Contact</h3>
                <p className="text-sm text-gray-600 mb-4">
                  For press inquiries, interviews, and media requests:
                </p>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="text-gray-500">Email:</span>{' '}
                    <a href="mailto:press@bestix.ai" className="text-navy font-medium">press@bestix.ai</a>
                  </p>
                  <p className="text-sm">
                    <span className="text-gray-500">Phone:</span>{' '}
                    <span className="text-navy font-medium">1-888-BESTIX-1</span>
                  </p>
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  Response within 24 hours for accredited media
                </p>
              </div>

              {/* Media Assets */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-semibold text-navy mb-4">Media Assets</h3>
                <div className="space-y-3">
                  {mediaAssets.map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <div className="text-sm font-medium text-navy">{asset.name}</div>
                          <div className="text-xs text-gray-400">{asset.format} • {asset.size}</div>
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Boilerplate */}
              <div className="bg-navy rounded-xl p-6">
                <h3 className="font-semibold text-white mb-3">About BESTIX</h3>
                <p className="text-sm text-gray-300">
                  BESTIX by BESPOKIAN is the premier luxury ticket marketplace connecting fans with verified sellers for the world's most sought-after events. With industry-leading buyer protection and a commitment to authenticity, BESTIX has facilitated over $500M in transactions across 50,000+ events.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
